import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    DateField,
    FunctionField,
    EditButton,
    DeleteButton,
    Filter,
    TextInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Chip,
    Box,
    Typography,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ElectricBolt,
    EvStation,
    FlashOn,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ChargerTypesFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate("resources.charger-types.list.filters.search")}
                source="search"
                alwaysOn
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
    </Box>
);

const PowerRangeDisplay = ({ record }: any) => {
    if (!record) return null;

    const minPower = record.minPowerKw || 0;
    const maxPower = record.maxPowerKw || '∞';

    return (
        <Typography variant="body2">
            {minPower} - {maxPower} kW
        </Typography>
    );
};

const ColorDisplay = ({ record }: any) => {
    if (!record || !record.colorCode) return null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 1,
                    backgroundColor: record.colorCode,
                    border: '1px solid #E0E0E0',
                }}
            />
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {record.colorCode}
            </Typography>
        </Box>
    );
};

const IconDisplay = ({ record }: any) => {
    if (!record || !record.icon) return null;

    const getIcon = (iconName: string) => {
        switch (iconName) {
            case 'ev-plug-type2':
                return <EvStation sx={{ fontSize: 20 }} />;
            case 'lightning-bolt':
                return <ElectricBolt sx={{ fontSize: 20 }} />;
            case 'flash-on':
                return <FlashOn sx={{ fontSize: 20 }} />;
            default:
                return <Typography variant="body2">{iconName}</Typography>;
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getIcon(record.icon)}
            <Typography variant="caption" color="text.secondary">
                {record.icon}
            </Typography>
        </Box>
    );
};

const DisplayNameChip = ({ record }: any) => {
    if (!record) return null;

    return (
        <Chip
            label={record.displayName}
            size="small"
            sx={{
                backgroundColor: record.colorCode ? `${record.colorCode}20` : '#E0E0E0',
                color: record.colorCode || '#000',
                fontWeight: 600,
                border: `1px solid ${record.colorCode || '#E0E0E0'}`,
            }}
        />
    );
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/charger-types/${record.id}`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                                {record.colorCode && (
                                    <Box sx={{ width: 16, height: 16, borderRadius: 0.5, backgroundColor: record.colorCode, border: '1px solid #E0E0E0', flexShrink: 0 }} />
                                )}
                                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {record.displayName || record.name}
                                </Typography>
                            </Box>
                            <Chip
                                label={`${record.minPowerKw || 0}-${record.maxPowerKw || '∞'} kW`}
                                size="small"
                                sx={{ flexShrink: 0, fontSize: '0.7rem', height: 22 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {record.id}
                            </Typography>
                            {record.icon && (
                                <Typography variant="caption" color="text.secondary">
                                    {record.icon}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                #{record.displayOrder}
                            </Typography>
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const ChargerTypesList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<ChargerTypesFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                sort={{ field: 'displayOrder', order: 'ASC' }}
                perPage={25}
                title={translate("resources.charger-types.name")}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': {
                            margin: 0,
                        },
                        '& .RaList-content': {
                            backgroundColor: 'transparent !important',
                            boxShadow: 'none !important',
                            mt: 2,
                        },
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                        '& .RaListToolbar-root': {
                            backgroundColor: 'transparent !important',
                            flexDirection: 'row !important',
                            flexWrap: 'wrap !important',
                            alignItems: 'center !important',
                            gap: '8px !important',
                        },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                        },
                        '& .filter-field, & .RaFilterForm-filterFormInput': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': {
                            backgroundColor: 'transparent !important',
                            minHeight: 'auto !important',
                        },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        rowClick="edit"
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField
                            source="id"
                            label={translate("resources.charger-types.fields.id")}
                            sx={{ fontFamily: 'monospace' }}
                        />

                        <TextField
                            source="name"
                            label={translate("resources.charger-types.fields.name")}
                        />

                        <FunctionField
                            label={translate("resources.charger-types.fields.displayName")}
                            render={(record: any) => <DisplayNameChip record={record} />}
                        />

                        <FunctionField
                            label={translate("resources.charger-types.fields.powerRange")}
                            render={(record: any) => <PowerRangeDisplay record={record} />}
                        />

                        <FunctionField
                            label={translate("resources.charger-types.fields.colorCode")}
                            render={(record: any) => <ColorDisplay record={record} />}
                        />

                        <FunctionField
                            label={translate("resources.charger-types.fields.icon")}
                            render={(record: any) => <IconDisplay record={record} />}
                        />

                        <NumberField
                            source="displayOrder"
                            label={translate("resources.charger-types.fields.displayOrder")}
                        />

                        <DateField
                            source="updatedAt"
                            label={translate("resources.charger-types.fields.updatedAt")}
                            showTime
                        />

                        <EditButton label="" />
                        <DeleteButton label="" />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/charger-types/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
